import { ConditionAttributes, ConditionAttributeValue } from "@evergis/condition";

import { FeatureAttribute } from "../../../modules/FeatureCard/components/Attributes/FeatureAttributeField";
import {
  getAttributeNameFromClassified,
  getAttributeNameFromCondition,
  isParameterByAttribute,
} from "../classification/getFromClassified";
import { evaluateCondition } from "../condition/evaluateCondition";
import { isObject } from "../../../utils/isObject";
import { ClassifiedParameterValue, ParameterByAttributeValues, StyleSymbolType } from "../../../interfaces/style";
import { Style } from "../../../ducks/style/style";
import { StyleProxy } from "../classification/StyleProxy";

function getAttributeValue(attributes: FeatureAttribute[], attributeName: string) {
  const attributeMeta = attributes.find(({ name }) => name === attributeName);

  return attributeMeta ? attributeMeta.value : null;
}

function findFeatureValue(
  parameterValues: ParameterByAttributeValues,
  [name, value]: [string, ConditionAttributeValue],
) {
  return value !== null
    ? parameterValues.find(({ condition }) => evaluateCondition(condition, { [name]: value }))
    : null;
}

function extractValuesAttribute(
  parameterValues: ParameterByAttributeValues,
  attributes: FeatureAttribute[],
): [string, ConditionAttributeValue] {
  const name = getAttributeNameFromClassified(parameterValues);
  const value = getAttributeValue(attributes, name);

  return [name, value as ConditionAttributeValue];
}

function evaluateFeatureSymbol(attributes: FeatureAttribute[]) {
  function calc(result: StyleSymbolType, [parameterName, parameterValue]: [string, ClassifiedParameterValue]) {
    let newParameterValue;

    if (isParameterByAttribute(parameterValue)) {
      const [attributeName, attributeValue] = extractValuesAttribute(parameterValue.values, attributes);
      const featureValue = findFeatureValue(parameterValue.values, [attributeName, attributeValue]);

      newParameterValue = featureValue ? featureValue.value : parameterValue.defaultValue;
    } else if (isObject(parameterValue)) {
      newParameterValue = Object.entries(parameterValue).reduce(calc, parameterValue);
    }

    return newParameterValue !== undefined
      ? {
          ...result,
          [parameterName]: newParameterValue,
        }
      : result;
  }

  return calc;
}

export function findChildFeatureStyle(childStyles: Style[], attributes: FeatureAttribute[]) {
  return (
    (childStyles || []).find(({ condition }) => {
      const attributeName = getAttributeNameFromCondition(condition);
      const attributeValue = getAttributeValue(attributes, attributeName);

      return evaluateCondition(condition, { [attributeName]: attributeValue } as ConditionAttributes);
    }) || null
  );
}

export function findChildFeatureSymbol(childStyles: Style[], attributes: FeatureAttribute[]) {
  const featureStyle = findChildFeatureStyle(childStyles, attributes);

  return featureStyle ? featureStyle.symbol : null;
}

export function getFeatureSymbol(style: StyleProxy, attributes: FeatureAttribute[]): StyleSymbolType | null {
  if (!style || !style.symbol) {
    return null;
  }

  if (style.children !== null) {
    return findChildFeatureSymbol(style.children, attributes) || style.symbol;
  }

  return Object.entries(style.symbol).reduce(evaluateFeatureSymbol(attributes), style.symbol);
}
